"use client"

import { useState, useEffect, useRef } from "react"
import styles from "./OTPModal.module.css"
import { useSelector } from "react-redux"
import axiosInstance from "../../../axios"

const OTPModal = ({
  isOpen,
  onClose,
  onSubmit,
  type,
  shift,
  disputeData,
  bulkApproveIds,
  verifyToken,
  get_Token,
  refreshShiftList,
  onSuccess, // Add onSuccess prop
  handleResetFilters,
}) => {
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (isOpen) {
      setOtp(["", "", "", ""]);
      setError("");
      setIsSubmitting(false);
      // Focus first input when modal opens
      setTimeout(() => {
        if (inputRefs.current[0]) {
          inputRefs.current[0].focus();
        }
      }, 100);
    }
  }, [isOpen]);

  const handleInputChange = (index, value) => {
    if (value.length > 1) return; // Prevent multiple characters

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp); // Update state immediately
    setError("");

    // Auto-focus next input
    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 4);
    const newOtp = [...otp];

    for (let i = 0; i < pastedData.length && i < 4; i++) {
      if (/^\d$/.test(pastedData[i])) {
        newOtp[i] = pastedData[i];
      }
    }

    setOtp(newOtp);
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otpString = otp.join("");

    if (otpString.length !== 4) {
      setError("Please enter complete 4-digit OTP");
      return;
    }

    if (!/^\d{4}$/.test(otpString)) {
      setError("OTP must be 4 digits");
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare the base request body
      const requestBody = {
        otp: otpString,
        verifyOtpToken: verifyToken?.data,
        data: [],
      };

      // Add shift data based on the type of action
      if (type === "bulkApprove") {
        const status = "APPROVED";
        requestBody.data = bulkApproveIds.map((id) => ({
          jobId: id.jobId,
          userId: id.userId,
          approved: status,
          disputedReason: "",
          disputedComment: "",
        }));
      } else if (type === "singleApprove" && shift) {
        requestBody.data = [
          {
            jobId: shift.jobId || "",
            userId: shift.userId || "",
            approved: "APPROVED",
            disputedReason: "",
            disputedComment: "",
          },
        ];
      } else if (type === "dispute" && disputeData) {
        requestBody.data = [
          {
            jobId: shift?.jobId || "",
            userId: shift?.userId || "",
            approved: "DISPUTED",
            disputedReason: disputeData.category || "",
            disputedComment: disputeData.comment || "",
          },
        ];
      } else if (type === "resolve") {
        requestBody.data = [
          {
            jobId: shift?.jobId || "",
            userId: shift?.userId || "",
            approved: "RESOLVED",
            disputedReason: shift?.disputedReason || "",
            disputedComment: shift?.disputedComment || "",
          },
        ];
      }

      // Call verifyOtp API with the prepared data

      // Call OTP API with the shift in an array
      const response = await axiosInstance.post(
        `/common/verifyOtp`,
        requestBody
      );
      // const response = await fetch('https://jobizo-admin.jobizo.com/common/verifyOtp', {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //     'Authorization': `Bearer ${get_Token()}`
      //   },
      //   body: JSON.stringify(requestBody),
      // });

      // if (!response.ok) {
      //   throw new Error('OTP verification failed');
      // }
      if (response) {
        // Call onSuccess callback if provided
        if (onSuccess) {
          onSuccess();
        }
        // Refresh the shift list after successful operation
        if (refreshShiftList) {
          refreshShiftList();
          handleResetFilters();
        }

        // If OTP verification is successful, call the original onSubmit
        // if (type === "singleApprove") {
        //   onSubmit(shift.id);
        // } else if (type === "bulkApprove") {
        //   onSubmit(bulkApproveIds);
        // } else if (type === "dispute") {
        //   onSubmit(disputeData.category, disputeData.comment);
        // }

        onClose();
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setError("Invalid OTP. Please try again.");
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.otpModalOverlay} onClick={handleOverlayClick}>
      <div className={styles.otpModal}>
        <div className={styles.otpModalHeader}>
          <h2>Enter OTP</h2>
          <button
            className={styles.closeBtn}
            onClick={onClose}
            disabled={isSubmitting}
          >
            ×
          </button>
        </div>

        <div className={styles.otpModalBody}>
          <form onSubmit={handleSubmit}>
            <div className={styles.otpSection}>
              <label>Enter 4-digit OTP</label>
              <div className={styles.otpInputs}>
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleInputChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    className={styles.otpInput}
                    disabled={isSubmitting}
                  />
                ))}
              </div>
              {error && <div className={styles.errorMessage}>{error}</div>}
            </div>

            {/* <div className={styles.demoInfo}>
              <p>Demo OTP: <strong>1234</strong></p>
            </div> */}

            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.cancelBtn}
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={styles.submitBtn}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Verifying..." : "Verify OTP"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OTPModal
